import { Container, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { gsap } from "gsap";
import { BasePageView } from "../../../../../common/components/BasePageView";
import { HourlyBonusResourcePackage } from "../../resources/HourlyBonusResourcePackage";
import { SoundsResourcePackageWrapper } from "../../../../../common/resources/SoundsResourcePackageWrapper";
import { IAnalyticProviderWrapper } from "../../../../../../../analytics/IAnalyticProviderWrapper";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { IFortuneWheelController } from "../../controller/IFortuneWheelController";
import { IFortuneWheelModel } from "../../../model/IFortuneWheelModel";
import { PromiseCompletionSource } from "../../../../../../../shared/async/PromiseCompletionSource";
import { ButtonWithScaleAnimation } from "../../../../../common/components/ButtonWithScaleAnimation";
import { WheelErrorMessage } from "../../components/WheelErrorMessage";
import { InputBlocker } from "../../../../../game/components/InputBlocker";
import { VirtualScreenEvent } from "../../../../../../../analytics/events/VirtualScreenEvent";
import { GameFont } from "../../../../../common/GameFont";
import { LightsBgContainer } from "../../components/goldenwheel/LightsBgContainer";
import { GoldenWheelSpinButton } from "../../components/goldenwheel/GoldenWheelSpinButton";
import { XMoreContainer } from "../../components/goldenwheel/XMoreContainer";
import { GoldenWheel } from "../../components/goldenwheel/GoldenWheel";
import { IGoldenWheelModel } from "../../../model/IGoldenWheelModel";
import { SpinButtonState } from "../../components/goldenwheel/SpinButtonState";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { PaymentResult } from "../../../../../../../web3/PaymentResult";
import { CancellationToken } from "async/CancellationToken";
import { nameof } from "../../../../../../../shared/bindable-data";
import { WedgeRewardData } from "../../../model/WedgeRewardData";
import { WedgeRewardType } from "../../../model/WedgeRewardType";
import { ViewCompletionSource } from "../../../../../../../scene/viewable/ViewCompletionSource";
import { WheelDiceRollsPage } from "./WheelDiceRollsPage";
import { IPlayerModel } from "../../../../../common/models/IPlayerModel";

export class GoldenWheelPage extends BasePageView {
    private static readonly WHEEL_ERROR_MESSAGE_TEXT = "WE CAN'T PROCESS\nYOUR PURCHASE REQUEST,\nPLEASE TRY AGAIN.";
    private static readonly BACK_TEXT = "GOLDEN WHEEL";
    private static readonly X_MORE_CONTAINER_SCALE = 0.65;
    private static readonly PAYMENT_TEXT = '*The prices above may incur additional payment charges';

    private readonly commonResources: CommonResourcesPackage;
    private readonly cancellationToken: CancellationToken;
    private readonly wheelResources: HourlyBonusResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly fortuneWheelModel: IFortuneWheelModel;
    private readonly goldenWheelModel: IGoldenWheelModel;
    private readonly playerModel: IPlayerModel;
    private readonly fortuneWheelController: IFortuneWheelController;
    private readonly viewCompletionSource: PromiseCompletionSource<void>;
    private readonly logger: ILogger;
    private background: Sprite;
    private lightBgContainer: LightsBgContainer;
    private fadeScreen: FadeScreen;
    private xMoreFadeScreen: FadeScreen;
    private goldenWheel: GoldenWheel;
    private spinButton: GoldenWheelSpinButton;
    private xMoreBackLayer: Container;
    private xMoreContainer: XMoreContainer;
    private backButton: ButtonWithScaleAnimation;
    private headerText: Text;
    private wheelErrorContainer: WheelErrorMessage;
    private InputBlocker: InputBlocker;
    private paymentText: Text;

    public constructor(
        commonResources: CommonResourcesPackage,
        cancellationToken: CancellationToken,
        wheelResources: HourlyBonusResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        canvasScaler: ICanvasScaler,
        analyticsProvider: IAnalyticProviderWrapper,
        fortuneWheelModel: IFortuneWheelModel,
        fortuneWheelController: IFortuneWheelController,
        playerModel: IPlayerModel,
        viewCompletionSource: PromiseCompletionSource<void>,
        logger: ILogger
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.cancellationToken = cancellationToken;
        this.wheelResources = wheelResources;
        this.soundResources = soundResources;
        this.analyticsProvider = analyticsProvider;
        this.fortuneWheelModel = fortuneWheelModel;
        this.goldenWheelModel = fortuneWheelModel.goldenWheelModel.value;
        this.fortuneWheelController = fortuneWheelController;
        this.playerModel = playerModel;
        this.viewCompletionSource = viewCompletionSource;
        this.logger = logger;
        this.build();
        this.goldenWheelModel.propertyChanged.add(this.onGoldenWheelModelPropertyChanged, this);
        this.once("added", this.show);
    }

    public async start(): Promise<void> { }

    private async onGoldenWheelModelPropertyChanged(model: IGoldenWheelModel, key: string): Promise<void> {
        if (key === nameof(model, "isPaid")) {
            if (model.isPaid.value) {
                this.spinButton.updateFromPaymentStatus(SpinButtonState.SPIN_NOW);
            }
        }
    }

    protected override build(): void {
        this.background = new Sprite(this.wheelResources.assets_views_hourly_bonus_golden_wheel_night_bg_png);
        this.background.eventMode = "static";
        this.background.anchor.set(0.5, 0.5);
        this.addChild(this.background);
        this.lightBgContainer = new LightsBgContainer(this.wheelResources);
        this.addChild(this.lightBgContainer);
        this.buildButtons();
        this.paymentText = new Text(GoldenWheelPage.PAYMENT_TEXT, new PaymentInfoTextStyle());
        this.addChild(this.paymentText);
        this.xMoreBackLayer = new Container();
        this.addChild(this.xMoreBackLayer);
        this.fadeScreen = new FadeScreen(0, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.fadeScreen);
        this.goldenWheel = new GoldenWheel(this.wheelResources, this.commonResources, this.soundResources, this.goldenWheelModel, this.canvasScaler, this.logger);
        this.addChild(this.goldenWheel);
        this.xMoreFadeScreen = new FadeScreen(0.5, 0, 0.75, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.addChild(this.xMoreFadeScreen);
        this.xMoreContainer = new XMoreContainer(this.wheelResources, this.soundResources, this.canvasScaler);
        this.addChild(this.xMoreContainer);
        this.wheelErrorContainer = new WheelErrorMessage(this.commonResources, this.soundResources, GoldenWheelPage.WHEEL_ERROR_MESSAGE_TEXT, this.hideError.bind(this));
        this.addChild(this.wheelErrorContainer);
        this.InputBlocker = new InputBlocker();
        this.addChild(this.InputBlocker);
        super.build();
    }

    private buildButtons(): void {
        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.onExitClick, this);
        this.addChild(this.backButton);
        this.headerText = new Text(GoldenWheelPage.BACK_TEXT, new BackTextStyle());
        this.headerText.anchor.set(0.5, 0.5);
        this.addChild(this.headerText);
        this.spinButton = new GoldenWheelSpinButton(this.wheelResources, this.fortuneWheelModel);
        this.spinButton.onSpinClicked.add(this.onSpinClick, this);
        this.addChild(this.spinButton);
    }

    private async show(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.InputBlocker.blocked = true;
        animationTimeLine.fromTo(this.backButton, { x: this.canvasScaler.getSize().width * 0.9, alpha: 0 }, { x: this.backButton.x, alpha: 1, duration: 0.3, ease: "power3.inout" });
        animationTimeLine.fromTo(this.headerText, { alpha: 0 }, { alpha: 1, duration: 0.1 }, "<80%");
        animationTimeLine.add(() => this.xMoreFadeScreen.fadeIn());
        animationTimeLine.set(this.xMoreContainer, { x: this.goldenWheel.x, y: this.goldenWheel.y });
        animationTimeLine.fromTo(this.xMoreContainer.scale, { x: 0, y: 0 }, {
            x: 1,
            y: 1,
            duration: 1.5,
            delay: 0.1,
            ease: "elastic.out(1, 0.4)"
        });
        animationTimeLine.add(() => this.xMoreContainer.playGlowAnimation(), "<60%");
        animationTimeLine.to(this.xMoreContainer, { x: this.xMoreContainer.x, y: this.xMoreContainer.y, duration: 0.5, delay: 1.3, ease: "power1.inOut" });
        animationTimeLine.add(() => this.xMoreFadeScreen.fadeOut(), "<");
        animationTimeLine.to(this.xMoreContainer.scale, { x: GoldenWheelPage.X_MORE_CONTAINER_SCALE, y: GoldenWheelPage.X_MORE_CONTAINER_SCALE, duration: 0.5, ease: "power1.inOut" }, "<");
        await animationTimeLine.play();
        animationTimeLine.kill();
        this.InputBlocker.blocked = false;
    }

    private async hidePage(): Promise<void> {
        this.InputBlocker.blocked = true;
        this.xMoreContainer.hideGlow();
        this.goldenWheel.hideWheelFrameAnimation();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
        this.viewCompletionSource.complete();
    }

    private async onSpinClick(): Promise<void> {
        this.InputBlocker.blocked = true;
        if (this.goldenWheelModel.isPaid.value) {
            this.soundResources.assets_sounds_goldenwheel_new_wheel_golden_wheel_spin_button_v2_mp3.play();
            this.goldenWheel.playWheelGlowAnimation();
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Spin wheel", this.analyticsProvider.currentEventIndex));
            const rewardData = await this.fortuneWheelController.claimGoldenWheelReward();
            await this.goldenWheel.rotateWheel();
            await this.showWheelClaimResultPopup(rewardData);
            await this.fortuneWheelController.loadGoldenWheelStatus();
            this.viewCompletionSource.complete();
        } else {
            this.soundResources.assets_sounds_goldenwheel_new_wheel_purchase_button_click_mp3.play();
            this.spinButton.updateFromPaymentStatus(SpinButtonState.PROCESS);
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Buy golden wheel button", this.analyticsProvider.currentEventIndex));
            const status = await this.fortuneWheelController.buyGoldenWheel(this.goldenWheelModel.tokenId.value, this.goldenWheelModel.price.value, this.cancellationToken);
            await this.fortuneWheelController.loadGoldenWheelStatus();
            switch (status) {
                case PaymentResult.SUCCESS:
                    this.spinButton.updateFromPaymentStatus(SpinButtonState.SPIN_NOW);
                    break;
                case PaymentResult.FAILURE:
                    if (!this.goldenWheelModel.isPaid.value) {
                        this.spinButton.updateFromPaymentStatus(SpinButtonState.FAILURE);
                    }
                case PaymentResult.CANCELLED:
                    if (!this.goldenWheelModel.isPaid.value) {
                        this.spinButton.updateFromPaymentStatus(SpinButtonState.FAILURE);
                    }
                    break;
            }
        }
        this.InputBlocker.blocked = false;
    }

    private async showWheelClaimResultPopup(rewardData: WedgeRewardData): Promise<void> {
        switch (rewardData.type) {
            case WedgeRewardType.DiceRoll:
                const diceRollsPageCS = new PromiseCompletionSource<void>();
                const wheelDiceRollsPage = new WheelDiceRollsPage(
                    this.commonResources,
                    this.wheelResources,
                    this.soundResources,
                    this.canvasScaler,
                    this.playerModel,
                    diceRollsPageCS,
                    rewardData.count
                );
                this.addChild(wheelDiceRollsPage);
                await diceRollsPageCS.result;
                this.removeChild(wheelDiceRollsPage);
                break;
            case WedgeRewardType.Coin:
            case WedgeRewardType.Shield:
            default:
                this.logger.warn(`Reward popup for type: ${rewardData.type} - not implemented.`);
                break;
        }
    }

    private async hideError(): Promise<void> {
        const animationTimeLine = new gsap.core.Timeline();
        this.soundResources.assets_sounds_goldenwheel_new_wheel_X_click_on_purchase_fail_mp3.play();
        animationTimeLine.addPause(0.3);
        await animationTimeLine.play();
        this.fadeScreen.eventMode = "none";
        this.fadeScreen.fadeOut();
        this.swapChildren(this.xMoreBackLayer, this.xMoreContainer);
        animationTimeLine.add(this.goldenWheel.getHideErrorAnimation());
        animationTimeLine.add(this.wheelErrorContainer.getHideErrorMessageAnimation(), "<");
        animationTimeLine.then(() => {
            this.xMoreContainer.playGlowAnimation();
            animationTimeLine.kill();
        });
        animationTimeLine.play();
    }

    private async onExitClick(): Promise<void> {
        this.soundResources.assets_sounds_upgrade_click_back_arrow_mp3.play();
        this.hidePage();
    }

    protected override layout(width: number, height: number): void {
        if (height * this.canvasScaler.referenceRatio <= width) {
            this.background.width = width;
            this.background.height = width / this.canvasScaler.referenceRatio;
        } else {
            this.background.height = height;
            this.background.width = height * this.canvasScaler.referenceRatio;
        }
        this.background.position.set(width * 0.5, height * 0.5);
        this.InputBlocker.width = width;
        this.InputBlocker.height = height;
        this.lightBgContainer.position.set(width * 0.5, height * 0.68);
        this.background.position.set(width * 0.5, height * 0.5);
        this.backButton.position.set(71 - this.backButton.width * 0.5, 264 - this.backButton.height * 0.5);
        this.headerText.position.set(278, 267);
        this.spinButton.position.set(width * 0.5, 1492);
        this.goldenWheel.position.set(width * 0.5, 800);
        this.xMoreContainer.scale.set(GoldenWheelPage.X_MORE_CONTAINER_SCALE);
        this.xMoreContainer.position.set(width * 0.5, this.goldenWheel.y + this.goldenWheel.height * 0.18);
        this.xMoreBackLayer.position = this.xMoreContainer.position;
        this.wheelErrorContainer.position.set(this.goldenWheel.x, 1360);
        this.paymentText.position.set(width * 0.5 - this.paymentText.width * 0.5, 1690);
        super.layout(width, height);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.spinButton.onSpinClicked.remove(this.onSpinClick, this);
        this.goldenWheelModel.propertyChanged.remove(this.onGoldenWheelModelPropertyChanged, this);
        this.backButton.onUp.remove(this.onExitClick, this);
        super.destroy(options);
    }
}

class BackTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60
        }));
    }
}

class PaymentInfoTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            letterSpacing: 1.25
        });
    }
}
